import {useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {WORKFORCE_DASHBOARD_PAIRED_SECTION_CONTENT_MIN_HEIGHT} from './workforceDashboardLayoutConstants';
import {WorkforceDashboardRecurringSkillGapsDetailsDialog} from './WorkforceDashboardRecurringSkillGapsDetailsDialog';
import {WorkforceDashboardSectionCard} from './WorkforceDashboardSectionCard';
import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {EmptyStateCard} from '../../../../../components/EmptyStateCard/EmptyStateCard';
import {orangeColors} from '../../../../../layouts/theme/designSystem';
import {WorkforceDashboardRecurringSkillGapsMetricData} from '../../types/workforceDashboardTypes';

export interface Props {
  data: WorkforceDashboardRecurringSkillGapsMetricData | null;
  fillHeight?: boolean;
}

export const WorkforceDashboardRecurringSkillGaps = ({data, fillHeight}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDashboardPage.sections.recurringSkillGaps'});
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

  const hasTopFive = (data?.topFive.length ?? 0) > 0;
  const hasDrillDownData = (data?.byTargetProfile.length ?? 0) > 0;
  const isFullyEmpty = !data || (!hasTopFive && !hasDrillDownData);

  if (isFullyEmpty) {
    return (
      <WorkforceDashboardSectionCard title={t('title')} fillHeight={fillHeight}>
        <EmptyStateCard message={t('emptyMessage') as string} />
      </WorkforceDashboardSectionCard>
    );
  }

  const globalMaxCount = data.topFive[0]?.profileCount || 1;

  return (
    <>
      <WorkforceDashboardSectionCard
        title={t('title')}
        description={t('description') as string}
        fillHeight={fillHeight}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            minWidth: 0,
            minHeight: WORKFORCE_DASHBOARD_PAIRED_SECTION_CONTENT_MIN_HEIGHT,
            flex: fillHeight ? 1 : undefined,
          }}
        >
          {hasTopFive ? (
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
              {data.topFive.map((item) => (
                <Box key={item.skillName} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  <Typography variant='body2' sx={{flex: 1}}>
                    {item.skillName}
                  </Typography>
                  <Box sx={{width: 80, height: 6, bgcolor: orangeColors[5], borderRadius: '999px', overflow: 'hidden'}}>
                    <Box
                      sx={{
                        height: '100%',
                        width: `${(item.profileCount / globalMaxCount) * 100}%`,
                        bgcolor: orangeColors[100],
                      }}
                    />
                  </Box>
                  <Typography variant='caption' sx={{width: 20, textAlign: 'right'}}>
                    {item.profileCount}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant='body2' sx={{color: 'text.secondary'}}>
              {t('topFiveEmptyMessage')}
            </Typography>
          )}

          {hasDrillDownData && (
            <Box sx={{pt: 0.5, width: '100%'}}>
              <EmpionButton
                variant='secondary'
                size='small'
                fullWidth
                onClick={() => setIsDetailsDialogOpen(true)}
              >
                {t('viewDetailsButton')}
              </EmpionButton>
            </Box>
          )}
        </Box>
      </WorkforceDashboardSectionCard>

      {hasDrillDownData && (
        <WorkforceDashboardRecurringSkillGapsDetailsDialog
          open={isDetailsDialogOpen}
          groups={data.byTargetProfile}
          onClose={() => setIsDetailsDialogOpen(false)}
        />
      )}
    </>
  );
};
